
import React, { useState, useEffect } from 'react';
import { useDataQuery, useDataMutation, useDataEngine } from '@dhis2/app-runtime';
import { useAuth } from '../../auth/AuthContext';
import OrganisationAssignDrop from './OrganisationAssignDrop';
import {
  InputField,
  SingleSelect,
  SingleSelectOption,
  Button,
  CircularLoader,
  AlertBar,
  IconChevronDown24,
  IconChevronRight24,

} from '@dhis2/ui';
import { useNavigate } from 'react-router-dom';
import ObjectiveSharingSetup from './ObjectiveSharingSetup';


const PROGRAMS_QUERY = {
  programs: {
    resource: 'programs',
    params: {
      fields: 'id,name,programType',
      paging: false
    }
  }
};

const ORG_UNITS_QUERY = {
  orgUnits: {
    resource: 'organisationUnits',
    params: {
      fields: 'id,name,level,path',
      paging: false
    }
  }
};


const COMPANY_METADATA_PROGRAM = {
  ID: 'NkReudUAW4F',
  STAGE_ID: 'htIWgNQWEMm', 
  DATA_ELEMENTS: {
    EMAIL: 'NkCxPqLeDpq',
    ORG_TYPE: 'QIMf5a1r1Ot',
    FIN_START: 'U55MqwBcRAe',
    FIN_END: 'kgdk4NXu4f5',
    COMPANY_LOGO: 'muysgcGfkyN'
  }
};

const ORG_UNIT_QUERY = {
  orgUnit: {
    resource: 'organisationUnits',
    id: ({ id }) => id,
    params: {
      fields: [
        'id',
        'name',
        'shortName',
        'openingDate',
        'parent[id,name]',
        'attributeValues[attribute[id,name],value]',
        'children[id,name,children[id,name,children[id,name]]]'
      ].join(',')
    }
  }
};

const HAS_USERS_QUERY = {
  hasUsers: {
    resource: 'organisationUnits',
    id: ({ id }) => id,
    params: {
      fields: 'id,name',
    },
    data: ({ data }) => data,
  },
  users: {
    resource: 'users',
    params: ({ id }) => ({
      fields: 'id',
      filter: `organisationUnits.id:eq:${id}`,
      pageSize: 1,
      totalPages: true
    })
  }
};

const ORG_TYPE_QUERY = {
  orgTypes: {
    resource: 'optionSets/z6JnM4OcF11',
    params: { fields: 'options[id,name]' },
  },
};

const MUTATIONS = {
  CREATE_ORG_UNIT: {
    resource: 'organisationUnits',
    type: 'create',
    data: ({ name, parentId, level, openingDate }) => ({
      name: name.trim(),
      shortName: name.trim().substring(0, 50),
      openingDate: openingDate || new Date().toISOString().split('T')[0],
      level: level,
      parent: { id: parentId },
      featureType: "NONE",
    }),
  },
  UPDATE_ORG_UNIT: {
    resource: 'organisationUnits',
    type: 'update',
    id: ({ id }) => id,
    data: ({ data }) => data,
  },
  DELETE_ORG_UNIT: {
    resource: 'organisationUnits',
    type: 'delete',
    id: ({ id }) => id,
  },
  // NEW: Update user analytics scope
  UPDATE_USER_ANALYTICS_SCOPE: {
    resource: 'users',
    type: 'update',
    id: ({ userId }) => userId,
    data: ({ userData }) => userData,
    params: {
      mergeMode: 'REPLACE'
    }
  },
};

// This helper is compressing the file 
const compressImage = async (file, options = {}) => {
  const {
    maxWidth = 150,           // Max width in pixels
    maxHeight = 150,          // Max height in pixels
    quality = 0.6,            // JPEG quality (0-1)
    maxSizeKB = 100,          // Target max size in KB
    format = 'image/jpeg'     // Output format
  } = options;

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    
    reader.onload = (e) => {
      const img = new Image();
      img.src = e.target.result;
      
      img.onload = () => {
        try {
          // Calculate new dimensions while maintaining aspect ratio
          let { width, height } = img;
          
          if (width > height) {
            if (width > maxWidth) {
              height = Math.round(height * (maxWidth / width));
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width = Math.round(width * (maxHeight / height));
              height = maxHeight;
            }
          }
          
          // Create canvas and draw resized image
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          
          // Better image rendering
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          
          ctx.drawImage(img, 0, 0, width, height);
          
          // Try different quality levels if needed
          let compressedBase64 = canvas.toDataURL(format, quality);
          
          // Check if we need to reduce quality further
          const base64Size = Math.round((compressedBase64.length * 3) / 4); // Approximate size in bytes
          console.log(`📊 Compressed size: ${Math.round(base64Size / 1024)}KB`);
          
          // If still too large, recursively compress with lower quality
          if (base64Size > maxSizeKB * 1024 && quality > 0.2) {
            console.log('📉 Still too large, reducing quality further...');
            compressedBase64 = canvas.toDataURL(format, quality * 0.7);
          }
          
          resolve(compressedBase64);
        } catch (error) {
          reject(error);
        }
      };
      
      img.onerror = (error) => reject(new Error('Failed to load image'));
    };
    
    reader.onerror = (error) => reject(error);
  });
};

const MONTH_OPTIONS = [
  { value: '01', label: 'January' },
  { value: '02', label: 'February' },
  { value: '03', label: 'March' },
  { value: '04', label: 'April' },
  { value: '05', label: 'May' },
  { value: '06', label: 'June' },
  { value: '07', label: 'July' },
  { value: '08', label: 'August' },
  { value: '09', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' }
];

const FINANCIAL_REFERENCE_YEAR = 2000;

const normalizeFinancialBoundary = (value) => {
  if (!value) return '';

  const trimmedValue = String(value).trim();
  const monthDayMatch = trimmedValue.match(/^(\d{2})-(\d{2})$/);
  if (monthDayMatch) {
    return `${monthDayMatch[1]}-${monthDayMatch[2]}`;
  }

  const fullDateMatch = trimmedValue.match(/^\d{4}-(\d{2})-(\d{2})$/);
  if (fullDateMatch) {
    return `${fullDateMatch[1]}-${fullDateMatch[2]}`;
  }

  return '';
};

const parseFinancialBoundary = (value, rolloverYear = 0) => {
  const normalizedValue = normalizeFinancialBoundary(value);
  if (!normalizedValue) return null;

  const [month, day] = normalizedValue.split('-').map(Number);
  if (!month || !day) return null;

  const year = FINANCIAL_REFERENCE_YEAR + rolloverYear;
  
  // Create date using UTC
  const parsedDate = new Date(Date.UTC(year, month - 1, day));
  
  // Validate the date is correct
  if (
    parsedDate.getUTCFullYear() !== year ||
    parsedDate.getUTCMonth() !== month - 1 ||
    parsedDate.getUTCDate() !== day
  ) {
    return null;
  }
  
  return parsedDate;
};

const getFinancialBoundaryParts = (value) => {
  const normalizedValue = normalizeFinancialBoundary(value);
  if (!normalizedValue) {
    return { month: '', day: '' };
  }

  const [month, day] = normalizedValue.split('-');
  return { month, day };
};

const buildFinancialBoundary = (month, day) => {
  if (!month && !day) return '';
  if (!month || !day) return `${month || ''}-${day || ''}`.replace(/^-|-$/g, '');

  const dayNumber = Number(day);
  return `${month}-${String(dayNumber).padStart(2, '0')}`;
};

const updateFinancialBoundaryPart = (currentValue, part, nextValue) => {
  const { month, day } = getFinancialBoundaryParts(currentValue);
  return buildFinancialBoundary(
    part === 'month' ? nextValue : month,
    part === 'day' ? nextValue : day
  );
};

const buildFinancialDatesForStorage = (startValue, endValue) => {
  const normalizedStart = normalizeFinancialBoundary(startValue);
  const normalizedEnd = normalizeFinancialBoundary(endValue);
  const start = parseFinancialBoundary(normalizedStart);
  let end = parseFinancialBoundary(normalizedEnd);

  if (!start || !end) {
    return { start: '', end: '' };
  }

  let endYear = FINANCIAL_REFERENCE_YEAR;
  if (end <= start) {
    end = parseFinancialBoundary(normalizedEnd, 1);
    endYear = FINANCIAL_REFERENCE_YEAR + 1;
  }

  if (!end) {
    return { start: '', end: '' };
  }

  return {
    start: `${FINANCIAL_REFERENCE_YEAR}-${normalizedStart}`,
    end: `${endYear}-${normalizedEnd}`
  };
};


const useFinancialDateValidation = (startDate, endDate) => {
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const newErrors = {};

    if (startDate && endDate) {
      const normalizedStart = normalizeFinancialBoundary(startDate);
      const normalizedEnd = normalizeFinancialBoundary(endDate);
      const start = parseFinancialBoundary(normalizedStart);
      let end = parseFinancialBoundary(normalizedEnd);

      if (!start || !end) {
        newErrors.dateRange = 'Enter a valid month and day for both dates';
      } else {
        if (end <= start) {
          end = parseFinancialBoundary(normalizedEnd, 1);
        }

        if (!end || end <= start) {
          newErrors.dateRange = 'End date must come after start date in the annual cycle';
        }

        const diffDays = Math.round((end - start) / (1000 * 60 * 60 * 24));
        if (diffDays < 330 || diffDays > 370) {
          newErrors.duration = 'Financial year should span about 12 months';
        }
      }
    }

    setErrors(newErrors);
  }, [startDate, endDate]);

  return errors;
};

const OrganisationUpdate = () => {
  const engine = useDataEngine();
  const { user } = useAuth();
  const orgUnitId = user?.organisationUnits?.[0]?.id;
  const navigate = useNavigate();

  const { data, loading, error, refetch } = useDataQuery(ORG_UNIT_QUERY, {
    variables: { id: orgUnitId },
    skip: !orgUnitId,
    cachePolicy: 'no-cache', // Force fresh data on each load
  });

  const { data: orgTypeData, loading: orgTypeLoading } = useDataQuery(ORG_TYPE_QUERY);

  const [createOrgUnit] = useDataMutation(MUTATIONS.CREATE_ORG_UNIT);
  const [updateOrgUnitMutation, { loading: updating }] = useDataMutation(MUTATIONS.UPDATE_ORG_UNIT);
  const [deleteOrgUnitMutation] = useDataMutation(MUTATIONS.DELETE_ORG_UNIT);
  const [updateUserAnalytics] = useDataMutation(MUTATIONS.UPDATE_USER_ANALYTICS_SCOPE);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [organisationType, setOrganisationType] = useState('');
  const [departments, setDepartments] = useState([]);
  const [newDepartment, setNewDepartment] = useState('');
  const [expandedDepartments, setExpandedDepartments] = useState({});

  const [removedDeptIds, setRemovedDeptIds] = useState([]);
  const [removedSectionIds, setRemovedSectionIds] = useState([]);
  const [removedSubsectionIds, setRemovedSubsectionIds] = useState([]);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [rootOrgUnitId, setRootOrgUnitId] = useState(null);


  const [programs, setPrograms] = useState([]);
  const [allOrgUnits, setAllOrgUnits] = useState([]);
  const [programAssignments, setProgramAssignments] = useState({});
  const [programAssignmentLoading, setProgramAssignmentLoading] = useState(false);

    // Logo states
  const [companyLogo, setCompanyLogo] = useState(null)
  const [logoUploading, setLogoUploading] = useState(false)
  const [showLogoUploader, setShowLogoUploader] = useState(false)
  const fileInputRef = React.useRef(null)

  const [allOrgUnitsAssigned, setAllOrgUnitsAssigned] = useState(false);

  // NEW: Analytics scope state

  const [analyticsUnits, setAnalyticsUnits] = useState([]);
  const [deletingDepartment, setDeletingDepartment] = useState(null);

  const [financialYearStart, setFinancialYearStart] = useState('');
  const [financialYearEnd, setFinancialYearEnd] = useState('');


  // LOAD METADATA FROM PROGRAM EVENT (PRIMARY SOURCE)
  const loadCompanyMetadata = async (orgUnitId) => {
    if (!orgUnitId) return null;

    try {
      // Get enrollment for this org unit
      const enrollmentResponse = await engine.query({
        enrollments: {
          resource: 'enrollments',
          params: {
            ou: orgUnitId,
            program: COMPANY_METADATA_PROGRAM.ID,
            fields: 'enrollment,trackedEntityInstance',
            pageSize: 1,
            order: 'lastUpdated:desc'
          }
        }
      });

      const enrollment = enrollmentResponse.enrollments?.enrollments?.[0];
      if (!enrollment) {
        console.log('ℹ️ No enrollment found');
        return { email: '', orgType: '', finStart: '', finEnd: '', companyLogo: '' };
      }

      // Query events by enrollment
      const queryParams = {
        program: COMPANY_METADATA_PROGRAM.ID,
        programStage: COMPANY_METADATA_PROGRAM.STAGE_ID,
        enrollment: enrollment.enrollment,
        pageSize: 1,
        order: 'eventDate:desc',
        fields: 'event,dataValues[dataElement,value]'
      };

      let response = await engine.query({
        events: { resource: 'events', params: queryParams }
      });

      let event = response.events?.events?.[0];

      // ✅ AUTO-CREATE EMPTY EVENT IF NONE EXISTS
      if (!event) {
        console.log('ℹ️ No metadata event found, creating empty event...');
        
        const today = new Date().toISOString().split('T')[0];
        
        const createResponse = await fetch('/api/events', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            program: COMPANY_METADATA_PROGRAM.ID,
            programStage: COMPANY_METADATA_PROGRAM.STAGE_ID,
            orgUnit: orgUnitId,
            eventDate: today,
            status: 'COMPLETED',
            trackedEntityInstance: enrollment.trackedEntityInstance,
            enrollment: enrollment.enrollment,
            dataValues: []
          })
        });
        
        if (createResponse.ok) {
          console.log('✅ Empty metadata event created');
          // Query again to get the new event
          response = await engine.query({
            events: { resource: 'events', params: queryParams }
          });
          event = response.events?.events?.[0];
        } else {
          const errorText = await createResponse.text();
          console.warn('Could not create empty event:', errorText);
          return { email: '', orgType: '', finStart: '', finEnd: '', companyLogo: '' };
        }
      }

      // Map data values (existing code)
      const values = { event: event.event, email: '', orgType: '', finStart: '', finEnd: '', companyLogo: '' };
      event.dataValues?.forEach(dv => {
        switch (dv.dataElement) {
          case COMPANY_METADATA_PROGRAM.DATA_ELEMENTS.EMAIL:
            values.email = dv.value; break;
          case COMPANY_METADATA_PROGRAM.DATA_ELEMENTS.ORG_TYPE:
            values.orgType = dv.value; break;
          case COMPANY_METADATA_PROGRAM.DATA_ELEMENTS.FIN_START:
            values.finStart = dv.value; break;
          case COMPANY_METADATA_PROGRAM.DATA_ELEMENTS.FIN_END:
            values.finEnd = dv.value; break;
          case COMPANY_METADATA_PROGRAM.DATA_ELEMENTS.COMPANY_LOGO:
            values.companyLogo = dv.value; break;
        }
      });

      return values;
    } catch (error) {
      console.error('❌ Failed to load metadata:', error);
      return { email: '', orgType: '', finStart: '', finEnd: '', companyLogo: '' };
    }
 };

  // SAVE METADATA TO PROGRAM EVENT (REPLACES ORG UNIT ATTRIBUTES)
const saveCompanyMetadata = async (metadata) => {
  if (!orgUnitId) throw new Error('Org unit ID required');

  const METADATA_STAGE_ID = 'htIWgNQWEMm';
  
  const dataValues = [
    { dataElement: COMPANY_METADATA_PROGRAM.DATA_ELEMENTS.EMAIL, value: (metadata.email || '').trim() },
    { dataElement: COMPANY_METADATA_PROGRAM.DATA_ELEMENTS.ORG_TYPE, value: (metadata.orgType || '').trim() },
    { dataElement: COMPANY_METADATA_PROGRAM.DATA_ELEMENTS.FIN_START, value: metadata.finStart || '' },
    { dataElement: COMPANY_METADATA_PROGRAM.DATA_ELEMENTS.FIN_END, value: metadata.finEnd || '' },
    { dataElement: COMPANY_METADATA_PROGRAM.DATA_ELEMENTS.COMPANY_LOGO, value: companyLogo?.base64 || '' }
  ].filter(dv => dv.dataElement);

  const today = new Date().toISOString().split('T')[0];

  // Get TEI and enrollment (same as before)
  let teiId = null;
  let enrollmentId = null;

  try {
    const teiResponse = await engine.query({
      teis: {
        resource: 'trackedEntityInstances',
        params: {
          program: COMPANY_METADATA_PROGRAM.ID,
          ou: orgUnitId,
          ouMode: 'SELECTED',
          fields: 'trackedEntityInstance',
          pageSize: 1
        }
      }
    });
    teiId = teiResponse.teis?.trackedEntityInstances?.[0]?.trackedEntityInstance;
  } catch (error) {
    console.warn('TEI query error:', error);
  }

  if (!teiId) {
      console.log('Creating new TEI for org unit:', orgUnitId);
      const createTeiResponse = await fetch('/api/trackedEntityInstances', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          trackedEntityType: 'RwXRVqlEw3l',
          orgUnit: orgUnitId,
          attributes: []
        })
      });
      
      console.log('TEI creation response status:', createTeiResponse.status);
      const teiResult = await createTeiResponse.json();
      console.log('TEI creation full response:', JSON.stringify(teiResult, null, 2));
      
      if (!createTeiResponse.ok) {
        throw new Error(`TEI creation failed: ${createTeiResponse.status} - ${JSON.stringify(teiResult)}`);
      }
      
      teiId = teiResult?.response?.importSummaries?.[0]?.reference || 
        teiResult?.response?.uid || 
        teiResult?.uid;
      
      if (!teiId) {
        throw new Error('TEI creation returned no ID');
      }
  }

  console.log('✅ Using TEI ID:', teiId);

  try {
    const enrollmentResponse = await engine.query({
      enrollments: {
        resource: 'enrollments',
        params: {
          trackedEntityInstance: teiId,
          program: COMPANY_METADATA_PROGRAM.ID,
          ou: orgUnitId,
          fields: 'enrollment',
          pageSize: 1
        }
      }
    });
    enrollmentId = enrollmentResponse.enrollments?.enrollments?.[0]?.enrollment;
  } catch (error) {
    console.warn('Enrollment query error:', error);
  }

  if (!enrollmentId) {
    const createEnrollmentResponse = await fetch('/api/enrollments', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        trackedEntityInstance: teiId,
        program: COMPANY_METADATA_PROGRAM.ID,
        orgUnit: orgUnitId,
        enrollmentDate: today,
        incidentDate: today,
        status: 'ACTIVE'
      })
    });
    const enrollmentResult = await createEnrollmentResponse.json();
    enrollmentId = enrollmentResult?.response?.importSummaries?.[0]?.reference ||
                 enrollmentResult?.enrollment ||
                 enrollmentResult?.response?.uid;
  }

  // ✅ MOVED baseEventData HERE - before using it
  const baseEventData = {
    program: COMPANY_METADATA_PROGRAM.ID,
    programStage: METADATA_STAGE_ID,
    orgUnit: orgUnitId,
    eventDate: today,
    status: 'COMPLETED',
    trackedEntityInstance: teiId,
    enrollment: enrollmentId,
    dataValues: dataValues
  };

  let existingEventId = null;
  try {
    const existingEventsResponse = await engine.query({
      events: {
        resource: 'events',
        params: {
          program: COMPANY_METADATA_PROGRAM.ID,
          programStage: METADATA_STAGE_ID,
          enrollment: enrollmentId,
          fields: 'event',
          pageSize: 1
        }
      }
    });
    existingEventId = existingEventsResponse.events?.events?.[0]?.event;
  } catch (error) {
    console.warn('Could not query existing event:', error);
  }

  if (existingEventId) {
    // UPDATE existing event instead of DELETE+CREATE
    console.log('✏️ Updating existing metadata event:', existingEventId);
    const updateResponse = await fetch(`/api/events/${existingEventId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        ...baseEventData,
        event: existingEventId
      })
    });
    
    if (!updateResponse.ok) {
      const errorText = await updateResponse.text();
      throw new Error(`Update failed (${updateResponse.status}): ${errorText}`);
    }
    console.log('✅ Updated existing metadata event');
  } else {
    // Create NEW event
    console.log('➕ Creating new metadata event');
    const createResponse = await fetch('/api/events', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          program: COMPANY_METADATA_PROGRAM.ID,
          programStage: METADATA_STAGE_ID,
          orgUnit: orgUnitId,
          eventDate: today,
          status: 'COMPLETED',
          trackedEntityInstance: teiId,
          enrollment: enrollmentId,
          dataValues: dataValues
        })
      });
    
    if (!createResponse.ok) {
      const errorText = await createResponse.text();
      throw new Error(`Create failed (${createResponse.status}): ${errorText}`);
    }

    const eventResult = await createResponse.json();
    const newEventId = eventResult?.response?.importSummaries?.[0]?.reference ||
                   eventResult?.response?.uid ||
                   eventResult?.uid;

    console.log('✅ Created new metadata event');
  }
};

    // Handle logo upload - store as base64
 const handleLogoUpload = async (event) => {
      const file = event.target.files?.[0];
      if (!file) return;

      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file (JPEG, PNG, GIF, etc.)');
        return;
      }

      // Show initial file size
      const fileSizeKB = Math.round(file.size / 1024);
      console.log(`📁 Original file: ${fileSizeKB}KB, type: ${file.type}`);

      setLogoUploading(true);

      try {
        // Determine compression settings based on file type
        const isPNG = file.type === 'image/png';
        
        // Compress the image
        const compressedBase64 = await compressImage(file, {
          maxWidth: 150,
          maxHeight: 150,
          quality: isPNG ? 0.8 : 0.6, // PNGs need higher quality
          maxSizeKB: 50, // Target 50KB final size
          format: isPNG ? 'image/png' : 'image/jpeg'
        });

        // Check final size
        const finalSizeKB = Math.round((compressedBase64.length * 3) / 4 / 1024);
        console.log(`✅ Final compressed size: ${finalSizeKB}KB`);
        
        if (finalSizeKB > 100) {
          console.warn(`⚠️ Final size (${finalSizeKB}KB) is larger than recommended`);
        }

        // Validate against data element limit (assuming 50000 chars)
        if (compressedBase64.length > 48000) { // Leave some margin
          throw new Error('Compressed image still too large. Please try a smaller image.');
        }

        setCompanyLogo({
          preview: compressedBase64,
          base64: compressedBase64,
          originalName: file.name,
          compressedSize: finalSizeKB
        });
        
        setShowLogoUploader(false);
        
        // Show success message with size info
        alert(`Logo compressed successfully!\nOriginal: ${fileSizeKB}KB → Compressed: ${finalSizeKB}KB`);
        
      } catch (error) {
        console.error('❌ Logo processing failed:', error);
        alert(`Failed to process logo: ${error.message}`);
      } finally {
        setLogoUploading(false);
      }
    };

  // Convert file to base64
  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => resolve(reader.result)
      reader.onerror = (error) => reject(error)
    })
  }
  
  const uploadImage = async (file) => {
    const formData = new FormData()
    formData.append('file', file)

    try {
      // Upload the file
      const response = await fetch('/api/fileResources', {
        method: 'POST',
        body: formData,
        credentials: 'include'
      })

      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`Upload failed (${response.status}): ${errorText}`)
      }

      const data = await response.json()
      const fileResourceId = data.response?.fileResource?.id || data.id
      
      console.log('✅ Image uploaded, resource ID:', fileResourceId)
      
      // Wait for the file to be stored
      await waitForFileToBeStored(fileResourceId)
      
      return fileResourceId
    } catch (error) {
      console.error('Image upload error:', error)
      throw error
    }
  }

  // Wait for file to be stored in Database
  const waitForFileToBeStored = async (fileId, maxAttempts = 10) => {
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const response = await fetch(`/api/fileResources/${fileId}`, {
          credentials: 'include',
          headers: {
            'Accept': 'application/json'
          }
        })
        
        if (response.ok) {
          const fileData = await response.json()
          console.log(`📊 File status check ${attempt}:`, fileData.storageStatus)
          
          if (fileData.storageStatus === 'STORED') {
            console.log('✅ File is now stored and ready')
            return true
          }
        }
        
        // Wait before next attempt (exponential backoff)
        await new Promise(resolve => setTimeout(resolve, Math.min(1000 * Math.pow(1.5, attempt), 5000)))
      } catch (error) {
        console.warn(`Error checking file status (attempt ${attempt}):`, error)
      }
    }
    
    throw new Error(`File ${fileId} not stored after ${maxAttempts} attempts`)
  }

  const toggleDepartment = (index) => {
    setExpandedDepartments(prev => {
      // If clicking an already expanded department, collapse it
      if (prev[index]) {
        return { ...prev, [index]: false };
      }
      // Otherwise, collapse all and expand only this one
      const newExpanded = {};
      newExpanded[index] = true;
      return newExpanded;
    });
  };

  const { data: programsData, loading: programsLoading } = useDataQuery(PROGRAMS_QUERY);
  const { data: orgUnitsData, loading: orgUnitsLoading } = useDataQuery(ORG_UNITS_QUERY);


  const { data: userCheckData, loading: checkingUsers } = useDataQuery(HAS_USERS_QUERY, {
    lazy: true,
  });

  const getOpeningDate = (orgUnit, parentOpeningDate) => {
    let date = orgUnit?.openingDate || parentOpeningDate || new Date().toISOString().split('T')[0];
    if (Array.isArray(date)) {
      date = date[0] || new Date().toISOString().split('T');
    }
    if (typeof date === 'string' && date.length === 10) {
      return date;
    } else if (typeof date === 'string') {
      return date;
    }
    return new Date().toISOString();
  };

  useEffect(() => {
    if (programsData?.programs?.programs) {
      setPrograms(programsData.programs.programs);
    }
  }, [programsData]);

  // Force refetch org unit data on mount to ensure fresh data
  useEffect(() => {
    if (orgUnitId) {
      console.log('🔄 Force refetching org unit data...');
      refetch();
    }
  }, [orgUnitId, refetch]);

  // Helper to collect org unit IDs recursively (including self)
  const collectOrgUnitsWithChildren = (unit) => {
    let ids = [unit.id];
    if (unit.children && unit.children.length > 0) {
      unit.children.forEach(child => {
        ids = ids.concat(collectOrgUnitsWithChildren(child));
      });
    }
    return ids;
  };

  // NEW: Function to update analytics scope permanently
  const updateAnalyticsScope = async (higherLevelUnits) => {
    try {
      if (!user?.id) {
        throw new Error("No user ID found");
      }

      // Get current user data first
      const userResponse = await engine.query({
        user: {
          resource: `users/${user.id}`,
          params: {
            fields: 'id,dataViewOrganisationUnits[id,name],organisationUnits[id,name],userCredentials,userGroups,teams,dataViewMaxOrganisationUnitLevel'
          }
        }
      });

      const userData = userResponse.user;

      // Prepare update data - only change dataViewOrganisationUnits
      const updateData = {
        ...userData,
        dataViewOrganisationUnits: higherLevelUnits.map(id => ({ id }))
      };

      // Remove any circular references or unsupported fields
      delete updateData.userCredentials;
      delete updateData.userGroups;
      delete updateData.teams;

      const result = await updateUserAnalytics({
        userId: user.id,
        userData: updateData
      });

      console.log("Analytics scope updated successfully:", result);
      return true;

    } catch (error) {
      console.error("Failed to update analytics scope:", error);
      throw error;
    }
  };

  // Load available higher-level org units for analytics scope
  useEffect(() => {
    const loadHigherLevelUnits = async () => {
      if (data?.orgUnit) {
        const units = [];

        // Add immediate parent
        if (data.orgUnit.parent?.id) {
          units.push(data.orgUnit.parent.id);
        }

        // Add higher levels if available in the path
        if (data.orgUnit.path) {
          const pathIds = data.orgUnit.path.split('/').filter(id => id && id !== orgUnitId);
          units.push(...pathIds);
        }

        setAnalyticsUnits([...new Set(units)]); // Remove duplicates
      }
    };

    loadHigherLevelUnits();
  }, [data, orgUnitId]);



  const financialDateErrors = useFinancialDateValidation(financialYearStart, financialYearEnd);

  const saveAllProgramAssignments = async () => {
    setProgramAssignmentLoading(true);
    try {
      // ✅ Use ONLY program IDs for reliability
      const TARGET_PROGRAM_IDS = [
        'RSANdetbnWC', // Leadership & Management
        'bzvlzdKhtFb', // Position History
        'NkReudUAW4F', // (Add if confirmed needed)
        'yiT00XKrrHP'  // Performance Management Monitoring
      ];

      const targetPrograms = programs.filter(p => TARGET_PROGRAM_IDS.includes(p.id));

      if (!targetPrograms.length) {
        alert("No target programs found for assignment!");
        return;
      }

      // 💡 Helper: collect all org units in your subtree (including self)
      const collectOrgUnitsWithChildren = (unit) => {
        if (!unit) return [];
        let ids = [unit.id];
        if (unit.children && unit.children.length > 0) {
          unit.children.forEach(child => {
            ids = ids.concat(collectOrgUnitsWithChildren(child));
          });
        }
        return ids;
      };

      // Ensure data.orgUnit is available
      if (!data?.orgUnit) {
        alert("Org unit data not loaded. Please refresh the page.");
        return;
      }

      // Get your full org subtree (this is your assignment scope)
      const myOrgSubtree = collectOrgUnitsWithChildren(data.orgUnit);
      console.log("Assignment scope (my org subtree):", myOrgSubtree);

      for (const program of targetPrograms) {
        const programId = program.id;

        let selectedOrgUnitIds = programAssignments[programId] || [];

        // ✅ CLEAN invalid IDs
        selectedOrgUnitIds = selectedOrgUnitIds.filter(id => id && typeof id === 'string');
        if (selectedOrgUnitIds.length === 0 || programId === 'RSANdetbnWC') {
          console.log(`⚠️ Auto-assigning subtree to ${program.id} (was: ${selectedOrgUnitIds.length} units)`);
          selectedOrgUnitIds = [...myOrgSubtree];
        } else {
          selectedOrgUnitIds = selectedOrgUnitIds.filter(id => myOrgSubtree.includes(id));
    
            // Ensure your own org unit is included for critical programs
            if (programId === 'NkReudUAW4F' && !selectedOrgUnitIds.includes(orgUnitId)) {
              console.log(`⚠️ Adding ${orgUnitId} to ${programId} assignments`);
              selectedOrgUnitIds.push(orgUnitId);
            }
        }

        // Fetch currently assigned org units from backend
        const currentlyAssignedOrgUnits = await getCurrentlyAssignedOrgUnits(programId);
        const currentlyAssignedIds = currentlyAssignedOrgUnits.map(ou => ou.id);

        // Compute diffs
        const orgUnitsToAdd = selectedOrgUnitIds.filter(id => !currentlyAssignedIds.includes(id));
        //const orgUnitsToRemove = currentlyAssignedIds.filter(id => !selectedOrgUnitIds.includes(id));

        console.log(`🎯 Program: ${program.id}`);
        console.log("  ➕ To add (only):", orgUnitsToAdd);
        console.log("  🚫 Preserving all existing assignments:", currentlyAssignedIds);

       
        // Add new assignments
        for (const orgUnitId of orgUnitsToAdd) {
          try {
            await engine.mutate({
              resource: `programs/${programId}/organisationUnits/${orgUnitId}`,
              type: "create",
            });
            console.log(`✅ Added ${orgUnitId} to ${programId}`);
          } catch (error) {
            console.error(`❌ Failed to add ${orgUnitId} to ${programId}:`, error);
            if (!error.message?.includes("already exists")) {
              // Only throw if it's a real error (not duplicate)
              throw error;
            }
          }
        }
      }

      alert("Program assignments updated successfully!");

      await loadExistingProgramAssignments(); // refresh UI

    } catch (error) {
      console.error("💥 Error saving program assignments:", error);
      alert(`Failed to save program assignments: ${error.message}`);
    } finally {
      setProgramAssignmentLoading(false);
    }
  };


  // Helper function to get currently assigned org units
  const getCurrentlyAssignedOrgUnits = async (programId) => {
    try {
      const response = await engine.query({
        currentAssignments: {
          resource: `programs/${programId}`,
          params: {
            fields: 'organisationUnits[id,children[id,children[id,children[id]]]]',
            paging: false
          }
        }
      });

      const extractIds = (units) => {
        const ids = [];
        (units || []).forEach(unit => {
          if (unit && unit.id) { // ✅ Only push valid IDs
            ids.push(unit.id);
            if (unit.children && unit.children.length > 0) {
              ids.push(...extractIds(unit.children));
            }
          }
        });
        return ids;
      };

      const allIds = extractIds(response.currentAssignments?.organisationUnits || []);
      return allIds.map(id => ({ id })); // return consistent shape
    } catch (error) {
      console.error(`Failed to fetch assignments for program ${programId}:`, error);
      return [];
    }
  };


  useEffect(() => {
    if (orgUnitsData?.orgUnits?.organisationUnits) {
      setAllOrgUnits(orgUnitsData.orgUnits.organisationUnits);
    }
  }, [orgUnitsData]);


  const verifyBackendAssignments = async () => {
    try {
      const performanceProgram = programs.find(p => p.name === 'Performance Management Monitoring');
      if (!performanceProgram) return;

      // Get exact assignments from backend
      const response = await engine.query({
        programAssignments: {
          resource: `programs/${performanceProgram.id}`,
          params: {
            fields: 'organisationUnits[id,name]'
          }
        }
      });

      const backendAssignments = response.programAssignments?.organisationUnits || [];
      console.log('Backend assignments:', backendAssignments);

      return backendAssignments;

    } catch (error) {
      console.error('Error verifying backend assignments:', error);
      return [];
    }
  };

  // Call this function to see what's actually assigned
  useEffect(() => {
    if (programs.length > 0) {
      verifyBackendAssignments();
    }
  }, [programs]);

    // Clean up object URL when component unmounts or logo changes
    useEffect(() => {
      return () => {
        if (companyLogo?.preview) {
          URL.revokeObjectURL(companyLogo.preview)
        }
      }
    }, [companyLogo])

  // Add this useEffect to fetch root org unit ID
  useEffect(() => {
    const fetchRootOrgUnit = async () => {
      try {
        const response = await engine.query({
          orgUnits: {
            resource: 'organisationUnits',
            params: {
              fields: 'id,name',
              filter: 'level:eq:1',
              paging: false
            }
          }
        });

        setRootOrgUnitId(response?.organisationUnits?.[0]?.id);
      } catch (error) {
        console.warn('Could not fetch root organization unit:', error);
      }
    };

    if (engine) {
      fetchRootOrgUnit();
    }
  }, [engine]);

  useEffect(() => {
    if (!data?.orgUnit) return;

    const totalOrgUnits = collectOrgUnitsWithChildren(data.orgUnit).length;
    const assignedOrgUnitsCount = Object.values(programAssignments)
      .flat()
      .filter((id, index, array) => array.indexOf(id) === index)
      .length;
    setAllOrgUnitsAssigned(assignedOrgUnitsCount === totalOrgUnits && totalOrgUnits > 0);
  }, [programAssignments, data, orgUnitId]);


  // Replace the useEffect that sets initial expanded state:
  useEffect(() => {
    if (!data?.orgUnit) return;

    const totalOrgUnits = collectOrgUnitsWithChildren(data.orgUnit).length;
    const assignedOrgUnitsCount = Object.values(programAssignments)
      .flat()
      .filter((id, index, array) => array.indexOf(id) === index)
      .length;
    setAllOrgUnitsAssigned(assignedOrgUnitsCount === totalOrgUnits && totalOrgUnits > 0);

    // ✅ SET COMPANY NAME
    setName(data.orgUnit.name || '');


     loadCompanyMetadata(orgUnitId).then(values => {
        console.log('📥 Metadata loaded in useEffect:', values);
        if (values) {
          setEmail(values.email || '');
          setOrganisationType(values.orgType || '');
          
          // ✅ FIX: Normalize to MM-DD pattern on load
          let normalizedStart = normalizeFinancialBoundary(values.finStart);
          let normalizedEnd = normalizeFinancialBoundary(values.finEnd);
          
          // Extract just MM-DD if it's a full date (e.g., "2025-07-01" -> "07-01")
          const startMatch = normalizedStart.match(/\d{4}-(\d{2}-\d{2})/);
          if (startMatch) {
            normalizedStart = startMatch[1];
          }
          const endMatch = normalizedEnd.match(/\d{4}-(\d{2}-\d{2})/);
          if (endMatch) {
            normalizedEnd = endMatch[1];
          }
          
          setFinancialYearStart(normalizedStart);  // e.g., "07-01"
          setFinancialYearEnd(normalizedEnd);      // e.g., "06-30"
          
          // Handle logo - store base64 string
          if (values.companyLogo) {
            console.log('🖼️ Logo base64 found');
            setCompanyLogo({ 
              base64: values.companyLogo,
              preview: values.companyLogo 
            });
          } else {
            setCompanyLogo(null);
          }
        }
      });

    console.log('🔍 Full org unit structure:', data.orgUnit);
    console.log('📊 Department children:', data.orgUnit.children);

    const mapOrgUnitStructure = (units, level = 0) => {
      if (!units || !Array.isArray(units)) return [];
      return units.map(unit => {
        console.log(`📝 Mapping level ${level}:`, unit.name, 'children:', unit.children);
        const mappedUnit = {
          id: unit.id,
          name: unit.name,
          originalChildren: unit.children || []
        };
        if (level === 0) {
          return {
            ...mappedUnit,
            newSection: '',
            sections: mapOrgUnitStructure(unit.children, 1)
          };
        } else if (level === 1) {
          return {
            ...mappedUnit,
            newSubsection: '',
            subsections: mapOrgUnitStructure(unit.children, 2)
          };
        } else if (level === 2) {
          return mappedUnit;
        }
        return mappedUnit;
      });
    };

    const mappedDepartments = mapOrgUnitStructure(data.orgUnit.children, 0);
    console.log('🎯 Final mapped departments:', mappedDepartments);
    setDepartments(mappedDepartments);



    setExpandedDepartments({});


  }, [data, engine, orgUnitId]);


  useEffect(() => {
    if (programs.length === 0 || !data?.orgUnit) return;

    const loadAssignments = async () => {
      const storedAssignments = localStorage.getItem(`company_program_assignments_${orgUnitId}`);
      if (storedAssignments) {
        try {
          const parsed = JSON.parse(storedAssignments);
          setProgramAssignments(parsed.assignments || {});
        } catch (e) {
          console.warn('Failed to parse stored program assignments', e);
        }
      }

      const TARGET_PROGRAM_IDS = [
        'RSANdetbnWC', // Leadership → auto-assign only
        'bzvlzdKhtFb', // Position History
        'yiT00XKrrHP', // Performance Management ← THIS is your focus
        'NkReudUAW4F'  // Company Goals
      ];

      const myOrgSubtree = collectOrgUnitsWithChildren(data.orgUnit);
      const newAssignments = {};

      for (const program of programs) {
        if (!TARGET_PROGRAM_IDS.includes(program.id)) continue;

        if (program.id === 'RSANdetbnWC') {
          // Leadership: never load from backend → always auto-assign on save
          continue;
        }

        try {
          const response = await engine.query({
            program: {
              resource: `programs/${program.id}`,
              params: {
                fields: 'organisationUnits[id,children[id,children[id,children[id]]]]',
                paging: false
              }
            }
          });

          const extractIds = (units) => {
            const ids = [];
            (units || []).forEach(unit => {
              if (unit?.id) {
                ids.push(String(unit.id));
                if (unit.children?.length) ids.push(...extractIds(unit.children));
              }
            });
            return ids;
          };

          const backendIds = extractIds(response.program?.organisationUnits || []);
          const validIds = backendIds.filter(id => myOrgSubtree.includes(id));

          newAssignments[program.id] = validIds.length > 0 ? validIds : [...myOrgSubtree];

          console.log(`✅ Loaded/fallback assignment for ${program.name} (${program.id}):`, newAssignments[program.id].length, 'units');
        } catch (error) {
          console.warn(`⚠️ Failed to load ${program.name}, falling back to full subtree`);
          newAssignments[program.id] = [...myOrgSubtree];
        }
      }

      setProgramAssignments(newAssignments);
    };

    loadAssignments();
  }, [programs, data?.orgUnit]);

  //////////////TEST SUBSECTION BEHAVIOR///

  useEffect(() => {
    console.log('Current departments state:', departments);
    departments.forEach((dept, deptIndex) => {
      console.log(`Department ${deptIndex}:`, dept.name, 'sections:', dept.sections?.length);
      dept.sections?.forEach((section, secIndex) => {
        console.log(`  Section ${secIndex}:`, section.name, 'subsections:', section.subsections?.length);
      });
    });
  }, [departments]);
  ///////////////////////////////

  const normalizeOrgUnitName = (value) => value.trim().replace(/\s+/g, ' ').toLowerCase();

  const hasDuplicateSiblingName = (items, candidateName, currentIndex = null) => {
    const normalizedCandidate = normalizeOrgUnitName(candidateName);
    if (!normalizedCandidate) {
      return false;
    }

    return items.some((item, index) => {
      if (currentIndex !== null && index === currentIndex) {
        return false;
      }

      return normalizeOrgUnitName(item.name || '') === normalizedCandidate;
    });
  };

  // Department handlers
  const addDepartment = () => {
    const trimmedName = newDepartment.trim();
    if (!trimmedName) return;

    if (hasDuplicateSiblingName(departments, trimmedName)) {
      alert(`A department named "${trimmedName}" already exists under this organisation.`);
      return;
    }

    setDepartments([
      ...departments,
      { id: null, name: trimmedName, sections: [], newSection: '' }
    ]);
    setNewDepartment('');
  };

  const checkIfOrgUnitHasUsers = async (orgUnitId) => {
    try {
      const response = await engine.query({
        users: {
          resource: 'users',
          params: {
            fields: 'id',
            filter: `organisationUnits.id:eq:${orgUnitId}`,
            pageSize: 1,
            totalPages: true
          }
        }
      });

      // Check if there are any users
      const hasUsers = response.users?.pager?.total > 0 ||
        (response.users?.users && response.users.users.length > 0);

      console.log(`Department ${orgUnitId} has users: ${hasUsers}`);
      return hasUsers;

    } catch (error) {
      console.error("Error checking for users in org unit:", error);
      throw error;
    }
  };


  const removeDepartment = async (index, e) => {
    if (e && typeof e.stopPropagation === 'function') {
      e.stopPropagation();
    }

    const dept = departments[index];

    if (dept.id) {
      setDeletingDepartment(index);

      try {
        const hasUsers = await checkIfOrgUnitHasUsers(dept.id);
        setDeletingDepartment(null);

        if (hasUsers) {
          alert(`Cannot delete "${dept.name}" because it has staff members assigned to it.`);
          return;
        }

        setRemovedDeptIds([...removedDeptIds, dept.id]);
      } catch (error) {
        setDeletingDepartment(null);
        alert(`Could not verify if department has staff. Please try again.`);
        return;
      }
    }

    setDepartments(departments.filter((_, i) => i !== index));
  };

  const updateDepartmentName = (index, value) => {
    const trimmedValue = value.trim();
    if (trimmedValue && hasDuplicateSiblingName(departments, trimmedValue, index)) {
      alert(`A department named "${trimmedValue}" already exists under this organisation.`);
      return;
    }

    const newDeps = [...departments];
    newDeps[index].name = value;
    setDepartments(newDeps);
  };

  // Section handlers
  const addSection = (deptIndex) => {
    const newDeps = [...departments];
    const trimmedName = newDeps[deptIndex].newSection.trim();
    if (!trimmedName) return;

    if (hasDuplicateSiblingName(newDeps[deptIndex].sections, trimmedName)) {
      alert(`A section named "${trimmedName}" already exists under "${newDeps[deptIndex].name}".`);
      return;
    }

    newDeps[deptIndex].sections.push({ id: null, name: trimmedName, subsections: [], newSubsection: '' });
    newDeps[deptIndex].newSection = '';
    setDepartments(newDeps);
  };

  const removeSection = async (deptIndex, secIndex) => {
    const newDeps = [...departments];
    const sec = newDeps[deptIndex].sections[secIndex];

    if (sec.id) {
      try {
        const hasUsers = await checkIfOrgUnitHasUsers(sec.id);

        if (hasUsers) {
          alert(`Cannot delete section "${sec.name}" because it has staff members assigned to it. Please reassign or remove the staff members first.`);
          return;
        }

        setRemovedSectionIds([...removedSectionIds, sec.id]);
      } catch (error) {
        console.error("Error checking for users:", error);
        alert(`Could not verify if section has staff. Please try again.`);
        return;
      }
    }

    newDeps[deptIndex].sections.splice(secIndex, 1);
    setDepartments(newDeps);
  };

  const removeSubsection = async (deptIndex, secIndex, subIndex) => {
    const newDeps = [...departments];
    const sub = newDeps[deptIndex].sections[secIndex].subsections[subIndex];

    if (sub.id) {
      try {
        const hasUsers = await checkIfOrgUnitHasUsers(sub.id);

        if (hasUsers) {
          alert(`Cannot delete subsection "${sub.name}" because it has staff members assigned to it. Please reassign or remove the staff members first.`);
          return;
        }

        setRemovedSubsectionIds([...removedSubsectionIds, sub.id]);
      } catch (error) {
        console.error("Error checking for users:", error);
        alert(`Could not verify if subsection has staff. Please try again.`);
        return;
      }
    }

    newDeps[deptIndex].sections[secIndex].subsections.splice(subIndex, 1);
    setDepartments(newDeps);
  };

  const updateSectionName = (deptIndex, secIndex, value) => {
    const trimmedValue = value.trim();
    if (trimmedValue && hasDuplicateSiblingName(departments[deptIndex].sections, trimmedValue, secIndex)) {
      alert(`A section named "${trimmedValue}" already exists under "${departments[deptIndex].name}".`);
      return;
    }

    const newDeps = [...departments];
    newDeps[deptIndex].sections[secIndex].name = value;
    setDepartments(newDeps);
  };

  // Subsection handlers
  const addSubsection = (deptIndex, secIndex) => {
    const newDeps = [...departments];
    const section = newDeps[deptIndex].sections[secIndex];
    const trimmedName = section.newSubsection.trim();
    if (!trimmedName) return;

    if (hasDuplicateSiblingName(section.subsections, trimmedName)) {
      alert(`A subsection named "${trimmedName}" already exists under "${section.name}".`);
      return;
    }

    section.subsections.push({ id: null, name: trimmedName });
    section.newSubsection = '';
    setDepartments(newDeps);
  };


  const updateSubsectionName = (deptIndex, secIndex, subIndex, value) => {
    const section = departments[deptIndex].sections[secIndex];
    const trimmedValue = value.trim();
    if (trimmedValue && hasDuplicateSiblingName(section.subsections, trimmedValue, subIndex)) {
      alert(`A subsection named "${trimmedValue}" already exists under "${section.name}".`);
      return;
    }

    const newDeps = [...departments];
    newDeps[deptIndex].sections[secIndex].subsections[subIndex].name = value;
    setDepartments(newDeps);
  };



  const createNewOrgUnit = async (name, parentId, level, openingDate, isLevel5 = false) => {
      console.log(`Creating org unit: ${name}, parent: ${parentId}, level: ${level}, isLevel5: ${isLevel5}`);

      //  FIRST: Check if org unit already exists
      try {
          const existingResponse = await engine.query({
              orgUnits: {
                  resource: 'organisationUnits',
                  params: {
                      fields: 'id',
                      filter: [`name:eq:${name.trim()}`, `parent.id:eq:${parentId}`],
                      pageSize: 1
                  }
              }
          });
          
          const existingOrgUnit = existingResponse.orgUnits?.organisationUnits?.[0];
          if (existingOrgUnit?.id) {
              console.log(`⚠️ Org unit "${name}" already exists with ID: ${existingOrgUnit.id}, skipping creation`);
              return existingOrgUnit.id;
          }
      } catch (checkError) {
          console.warn('Could not check existing org unit, proceeding with creation:', checkError);
      }

      // Build base payload
      const payload = {
          name: name.trim(),
          shortName: name.trim().substring(0, 50),
          openingDate: openingDate || new Date().toISOString().split('T')[0],
          level: level,
          parent: { id: parentId },
          featureType: "NONE"
      };

      try {
          const response = await createOrgUnit({
              name: payload.name,
              parentId,
              level,
              openingDate: payload.openingDate
          });
          let newId;

          if (response?.response?.uid) {
              newId = response.response.uid;
          } else if (response?.response?.id) {
              newId = response.response.id;
          } else if (response?.uid) {
              newId = response.uid;
          } else if (response?.id) {
              newId = response.id;
          }

          if (!newId) {
              const fetchedId = await fetchOrgUnitIdByName(name, parentId);
              if (fetchedId) {
                  newId = fetchedId;
              } else {
                  throw new Error('Could not determine ID of created organization unit');
              }
          }

          console.log(`✅ Created org unit "${name}" with ID ${newId}`);
          return newId;

      } catch (error) {
          // If 409 conflict, try to fetch existing ID
          if (error.message?.includes('409') || error.status === 409) {
              console.log(`⚠️ 409 Conflict for "${name}", attempting to fetch existing...`);
              const existingId = await fetchOrgUnitIdByName(name, parentId);
              if (existingId) {
                  console.log(`✅ Found existing org unit "${name}" with ID: ${existingId}`);
                  return existingId;
              }
          }
          console.error(`Failed to create org unit "${name}":`, error);
          throw new Error(`Failed to create organization unit "${name}": ${error.message}`);
      }
  };

  // Add this helper function to fetch org unit ID by name
  const fetchOrgUnitIdByName = async (name, parentId) => {
    try {
      const response = await engine.query({
        orgUnits: {
          resource: 'organisationUnits',
          params: {
            fields: 'id',
            filter: [`name:eq:${name.trim()}`, `parent.id:eq:${parentId}`],
            pageSize: 1
          }
        }
      });

      return response?.orgUnits?.organisationUnits?.[0]?.id;
    } catch (error) {
      console.error('Error fetching org unit by name:', error);
      return null;
    }
  };


  const loadExistingProgramAssignments = async () => {
    try {
      // ✅ Use only IDs for reliability
      const TARGET_PROGRAM_IDS = [
        'RSANdetbnWC', // Leadership & Management
        'bzvlzdKhtFb', // Position History
        'yiT00XKrrHP', // Performance Management Monitoring
        'NkReudUAW4F'
      ];

      const targetPrograms = programs.filter(p => TARGET_PROGRAM_IDS.includes(p.id));
      if (!targetPrograms.length) return;

      const newAssignments = { ...programAssignments };

      for (const program of targetPrograms) {
        try {
          const response = await engine.query({
            programWithOrgunits: {
              resource: `programs/${program.id}`,
              params: {
                fields: "organisationUnits[id,children[id,children[id,children[id]]]]",
                paging: false
              },
            },
          });

          const assignedOrgUnits = response.programWithOrgunits?.organisationUnits || [];

          // Safely extract all valid org unit IDs (including children)
          const extractAllOrgUnitIds = (units) => {
            const allIds = [];
            (units || []).forEach(unit => {
              if (unit && unit.id) { // ✅ Only push valid IDs
                allIds.push(String(unit.id));
                if (unit.children && unit.children.length > 0) {
                  allIds.push(...extractAllOrgUnitIds(unit.children));
                }
              }
            });
            return allIds;
          };

          const assignedIds = extractAllOrgUnitIds(assignedOrgUnits);
          console.log(`Loaded IDs for ${program.name} (${program.id}):`, assignedIds);

          // ✅ For Position History: preserve auto-assignment if backend is empty
          if (program.id === 'RSANdetbnWC') {
            // Always use auto-assignment for Leadership program
            console.log(`🔄 Skipping backend load for Leadership program - will auto-assign`);
            // Do NOT set newAssignments[program.id] here → leaves it undefined
          } else if (assignedIds.length > 0 || program.id !== 'bzvlzdKhtFb') {
            newAssignments[program.id] = assignedIds.map((id) => String(id));
          } else {
            console.log(`🔄 Preserving existing assignment for Position History (backend empty)`);
            // Keep what's already in state (likely auto-assigned)
          }
        } catch (error) {
          console.error(`Error loading assignments for ${program.name} (${program.id}):`, error);
          // On error, preserve current state or initialize as empty
          if (newAssignments[program.id] === undefined) {
            newAssignments[program.id] = [];
          }
        }
      }

      setProgramAssignments(newAssignments);
    } catch (error) {
      console.error("Error loading program assignments:", error);
    }
  };


  const handleSubmit = async (e) => {
      e.preventDefault();
      setIsSubmitting(true);

      try {
        // ✅ FIX: Normalize to MM-DD pattern first
        let normalizedStart = normalizeFinancialBoundary(financialYearStart);
        let normalizedEnd = normalizeFinancialBoundary(financialYearEnd);
        
        // Extract just MM-DD if it's a full date
        const startMatch = normalizedStart.match(/\d{4}-(\d{2}-\d{2})/);
        if (startMatch) {
          normalizedStart = startMatch[1];
        }
        const endMatch = normalizedEnd.match(/\d{4}-(\d{2}-\d{2})/);
        if (endMatch) {
          normalizedEnd = endMatch[1];
        }
        
        // Validate using the normalized MM-DD patterns
        if (normalizedStart && normalizedEnd) {
          const startDate = parseFinancialBoundary(normalizedStart);
          let endDate = parseFinancialBoundary(normalizedEnd);
          if (startDate && endDate && endDate <= startDate) {
            endDate = parseFinancialBoundary(normalizedEnd, 1);
          }

          if (!startDate || !endDate || endDate <= startDate) {
            alert('Financial year end date must come after the start date in the annual cycle');
            setIsSubmitting(false);
            return;
          }
        }

        if (!normalizedStart || !normalizedEnd) {
          alert('Financial Year Start and End dates are required.');
          setIsSubmitting(false);
          return;
        }

        // Use the normalized patterns for storage
        const storedFinancialDates = buildFinancialDatesForStorage(normalizedStart, normalizedEnd);

        if (!storedFinancialDates.start || !storedFinancialDates.end) {
          alert('Financial Year Start and End dates are invalid.');
          setIsSubmitting(false);
          return;
        }

        // Update org unit structure
        const updateData = {
          name: name.trim(),
          shortName: name.trim().substring(0, 50),
          openingDate: data.orgUnit.openingDate || new Date().toISOString().split('T')[0],
          parent: data.orgUnit.parent ? { id: data.orgUnit.parent.id } : undefined
        };

        console.log('🚀 Updating org unit structure ONLY (no attributes):', updateData);
        await updateOrgUnitMutation({ id: orgUnitId, data: updateData });
        console.log('✅ Org unit structure updated');

        // Process departments, sections, subsections (create/update)
        const parentOpeningDate = data.orgUnit.openingDate || new Date().toISOString();
        
        for (const dept of departments) {
              let deptId = dept.id;
              const deptOpeningDate = getOpeningDate(dept, parentOpeningDate);

              if (!deptId) {
                  try {
                      deptId = await createNewOrgUnit(dept.name, orgUnitId, 6, deptOpeningDate);
                      dept.id = deptId;
                  } catch (error) {
                      console.error(`Failed to create department ${dept.name}:`, error);
                      // Try to fetch existing ID
                      const existingId = await fetchOrgUnitIdByName(dept.name, orgUnitId);
                      if (existingId) {
                          console.log(`Using existing department ID: ${existingId}`);
                          deptId = existingId;
                          dept.id = existingId;
                      } else {
                          throw error;
                      }
                  }
              } else {
                  await updateOrgUnitMutation({
                      id: deptId,
                      data: {
                          name: dept.name,
                          shortName: dept.name.substring(0, 50),
                          openingDate: deptOpeningDate,
                          parent: { id: orgUnitId }
                      }
                  });
           }

          for (const sec of dept.sections) {
            let secId = sec.id;
            const secOpeningDate = getOpeningDate(sec, deptOpeningDate);

            if (!secId) {
              secId = await createNewOrgUnit(sec.name, deptId, 7, secOpeningDate);
              sec.id = secId;
            } else {
              await updateOrgUnitMutation({
                id: secId,
                data: {
                  name: sec.name,
                  shortName: sec.name.substring(0, 50),
                  openingDate: secOpeningDate,
                  parent: { id: deptId },
                },
              });
            }

            for (const sub of sec.subsections) {
              let subId = sub.id;
              const subOpeningDate = getOpeningDate(sub, secOpeningDate);

              if (!subId) {
                subId = await createNewOrgUnit(sub.name, secId, 8, subOpeningDate);
                sub.id = subId;
              } else {
                await updateOrgUnitMutation({
                  id: subId,
                  data: {
                    name: sub.name,
                    shortName: sub.name.substring(0, 50),
                    openingDate: subOpeningDate,
                    parent: { id: secId },
                  },
                });
              }
            }
          }
        }

        try {
          await saveAllProgramAssignments();
          console.log("✅ Program assignments saved - program now linked to org units");
        } catch (error) {
          console.error("❌ Program assignments failed - cannot continue:", error);
          alert("Failed to assign programs. Please try again.");
          setIsSubmitting(false);
          return;
        }

        // SAVE METADATA TO PROGRAM EVENT
        console.log('💾 Saving company metadata to program event...');
        await saveCompanyMetadata({
          email,
          orgType: organisationType,
          finStart: storedFinancialDates.start,
          finEnd: storedFinancialDates.end
        });

        console.log('✅ Company updated with financial dates');

        // Delete removed units
        for (const id of removedSubsectionIds) await deleteOrgUnitMutation({ id });
        for (const id of removedSectionIds) await deleteOrgUnitMutation({ id });
        for (const id of removedDeptIds) await deleteOrgUnitMutation({ id });

        // ===== SUCCESS =====
        alert('✅ Company updated successfully!\n\n📧 Email, 🏢 Type, and 📅 Financial Calendar\nare now permanently saved)');
        window.location.reload();

      } catch (error) {
        console.error('💥 handleSubmit error:', error);
        alert(`Failed to save: ${error.message}\n\nCheck console for details`);
        setIsSubmitting(false);
      }
  };

  const renderFormField = (label, children, required = true) => (
    <div style={{ marginBottom: '10px' }}>
      <div style={{ fontWeight: '300', marginBottom: '2px' }}>
        {label} {required && <span style={{ color: 'red' }}>*</span>}
      </div>
      {children}
    </div>
  );

  // Wait for both org unit and org type data to be loaded
  if (loading || orgTypeLoading || !data?.orgUnit) {
    console.log('⏳ Waiting for data to load...', { loading, orgTypeLoading, hasData: !!data?.orgUnit });
    return <CircularLoader small />;
  }
  if (error) {
    console.error('❌ Error loading company data:', error);
    return <AlertBar critical>Failed to load company data</AlertBar>;
  }

  return (
    <form onSubmit={handleSubmit} style={{ maxWidth: 700, margin: 'auto', padding: 20 }}>
      <h3 style={{ color: 'green' }}>Edit your Company Information</h3>

      <div>
        <ObjectiveSharingSetup />
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '16px',
        marginBottom: '20px'
      }}>
        {renderFormField('Company/Business Name', (
          <InputField
            value={name}
            onChange={({ value }) => setName(value)}
            required
          />
        ))}
        {renderFormField('Email', (
          <InputField
            type="email"
            value={email}
            onChange={({ value }) => {
              console.log('🔧 Email onChange called with value:', value);
              setEmail(value);
            }}
            required
          />
        ))}

        {renderFormField('Type', (
          <InputField
            value={organisationType}
            onChange={({ value }) => {
              console.log('✏️ Org Type free text input:', value);
              setOrganisationType(value); // Stores raw string
            }}
            placeholder="e.g., Private Company, NGO, Government"
            required
          />
        ))}
      </div>
      <div style={{
        marginBottom: '30px',
        padding: '20px',
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
        backgroundColor: '#f9f9f9'
      }}>
        <h3 style={{ marginTop: 0, color: '#2c3e50' }}>Financial/Business Calendar</h3>
        <p style={{ color: '#666', fontSize: '14px', marginBottom: '20px' }}>
          Set the default financial year cycle for your company using month and day only. These dates will be used for
          Overall system performance management
        </p>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '16px'
        }}>
          <div>
            <div style={{ fontWeight: '300', marginBottom: '2px' }}>
              Financial Year Start <span style={{ color: 'red' }}>*</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px', gap: '8px' }}>
              <SingleSelect
                selected={getFinancialBoundaryParts(financialYearStart).month}
                onChange={({ selected }) => setFinancialYearStart(prev => updateFinancialBoundaryPart(prev, 'month', selected))}
                placeholder="Select month"
                error={!!financialDateErrors.dateRange}
              >
                {MONTH_OPTIONS.map(month => (
                  <SingleSelectOption key={month.value} value={month.value} label={month.label} />
                ))}
              </SingleSelect>
              <InputField
                type="number"
                min="1"
                max="31"
                value={getFinancialBoundaryParts(financialYearStart).day}
                onChange={({ value }) => setFinancialYearStart(prev => updateFinancialBoundaryPart(prev, 'day', value))}
                placeholder="Day"
                required
                error={!!financialDateErrors.dateRange}
              />
            </div>
          </div>

          <div>
            <div style={{ fontWeight: '300', marginBottom: '2px' }}>
              Financial Year End <span style={{ color: 'red' }}>*</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px', gap: '8px' }}>
              <SingleSelect
                selected={getFinancialBoundaryParts(financialYearEnd).month}
                onChange={({ selected }) => setFinancialYearEnd(prev => updateFinancialBoundaryPart(prev, 'month', selected))}
                placeholder="Select month"
                error={!!financialDateErrors.dateRange}
              >
                {MONTH_OPTIONS.map(month => (
                  <SingleSelectOption key={month.value} value={month.value} label={month.label} />
                ))}
              </SingleSelect>
              <InputField
                type="number"
                min="1"
                max="31"
                value={getFinancialBoundaryParts(financialYearEnd).day}
                onChange={({ value }) => setFinancialYearEnd(prev => updateFinancialBoundaryPart(prev, 'day', value))}
                placeholder="Day"
                required
                error={!!financialDateErrors.dateRange}
                validationText={financialDateErrors.dateRange}
              />
            </div>
          </div>
        </div>

        {financialDateErrors.duration && (
          <AlertBar warning style={{ marginTop: '10px' }}>
            Note: {financialDateErrors.duration}
          </AlertBar>
        )}

        {financialYearStart && financialYearEnd && financialDateErrors.dateRange && (
          <AlertBar warning style={{ marginTop: '10px' }}>
            {financialDateErrors.dateRange}
          </AlertBar>
        )}
      </div>


       {/* Company Logo Section */}
      <div style={{
        marginBottom: '30px',
        padding: '20px',
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
        backgroundColor: '#f9f9f9'
      }}>
        <h3 style={{ marginTop: 0, color: '#2c3e50' }}>Company Logo</h3>
        <p style={{ color: '#666', fontSize: '14px', marginBottom: '20px' }}>
          Upload your company logo. It will be displayed in the sidebar navigation.
        </p>

        {/* Hidden file input */}
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          accept="image/*"
          onChange={handleLogoUpload}
          disabled={logoUploading}
        />

        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
           {companyLogo ? (
            <div style={{
              width: '100px',
              height: '100px',
              border: '2px solid #ddd',
              borderRadius: '8px',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'white'
            }}>
              <img 
                src={companyLogo.preview} 
                alt="Company Logo Preview" 
                style={{ 
                  maxWidth: '100%', 
                  maxHeight: '100%',
                  objectFit: 'contain'
                }} 
              />
            </div>
          ) : (
            <div style={{
              width: '100px',
              height: '100px',
              border: '2px dashed #ddd',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#f5f5f5',
              color: '#999'
            }}>
              No logo
            </div>
          )}

          {/* Upload controls */}
          <div>
            {!showLogoUploader ? (
              <Button
                primary
                onClick={() => setShowLogoUploader(true)}
                style={{ marginBottom: '8px' }}
              >
                {companyLogo ? 'Change Logo' : 'Upload Logo'}
              </Button>
            ) : (
              <div>
                <Button
                  primary
                  onClick={() => fileInputRef.current?.click()}
                  disabled={logoUploading}
                  style={{ marginBottom: '8px'}}
                >
                  {logoUploading ? 'Uploading...' : 'Select Image'}
                </Button>
                <Button
                  destructive
                  onClick={() => setShowLogoUploader(false)}
                  style={{ marginLeft: '8px'}}
                >
                  Cancel
                </Button>
                <p style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                  Max size: 5MB. Supported formats: JPEG, PNG, GIF
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Departments section */}
      <div style={{ marginTop: 20 }}>
        <h3>Departments</h3>
        <div style={{ display: 'flex', gap: '8px', marginBottom: 8 }}>
          <InputField
            placeholder="New Department"
            value={newDepartment}
            onChange={({ value }) => setNewDepartment(value)}
          />
          <Button small primary onClick={addDepartment}>Add</Button>
        </div>

        {departments.map((dept, deptIndex) => {
           return (
            <div key={deptIndex} style={{ border: '1px solid #ccc', borderRadius: 4, marginBottom: 12 }}>
              <div
                onClick={() => toggleDepartment(deptIndex)}
                style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '8px 12px', backgroundColor: '#f0f0f0' }}
              >
                {expandedDepartments[deptIndex] ? <IconChevronDown24 /> : <IconChevronRight24 />}
                <InputField
                  label={`Department ${deptIndex + 1}`}
                  value={dept.name}
                  onChange={({ value }) => updateDepartmentName(deptIndex, value)}
                  style={{ flex: 1, marginLeft: 8 }}
                  required
                />
                <Button
                  small
                  destructive
                  onClick={(e) => removeDepartment(deptIndex, e)}
                  style={{ marginLeft: 8 }}
                  disabled={deletingDepartment === deptIndex}
                >
                  {deletingDepartment === deptIndex ? (
                    <CircularLoader small />
                  ) : (
                    "Remove"
                  )}
                </Button>
              </div>

              {expandedDepartments[deptIndex] && (
                <div style={{ padding: '12px 24px' }}>
                  <h4>Sections</h4>
                  <div style={{ display: 'flex', gap: '8px', marginBottom: 6 }}>
                    <InputField
                      placeholder="New Section"
                      value={dept.newSection || ''}
                      onChange={({ value }) => {
                        const copy = [...departments];
                        copy[deptIndex].newSection = value;
                        setDepartments(copy);
                      }}
                    />
                    <Button small primary onClick={() => addSection(deptIndex)}>Add</Button>
                  </div>

                  {dept.sections?.map((section, secIndex) => {
                    // Debug for Employee Relations section
                    if (section.name === 'Employee Relations') {
                      console.log('🎯 RENDERING Employee Relations Section, subsections:', section.subsections);
                    }

                    return (
                      <div key={secIndex} style={{ marginBottom: 10, paddingLeft: 12, borderLeft: '1px dashed #aaa' }}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <InputField
                            label={`Section ${secIndex + 1}`}
                            value={section.name}
                            onChange={({ value }) => updateSectionName(deptIndex, secIndex, value)}
                            style={{ flex: 1 }}
                            required
                          />
                          <Button small destructive onClick={() => removeSection(deptIndex, secIndex)} style={{ marginLeft: 8 }}>
                            Remove
                          </Button>
                        </div>

                        <div style={{ marginLeft: 20, marginTop: 6 }}>
                          <h5>Subsections</h5>
                          <div style={{ display: 'flex', gap: '8px', marginBottom: 4 }}>
                            <InputField
                              placeholder="New Subsection"
                              value={section.newSubsection || ''}
                              onChange={({ value }) => {
                                const copy = [...departments];
                                copy[deptIndex].sections[secIndex].newSubsection = value;
                                setDepartments(copy);
                              }}
                            />
                            <Button small primary onClick={() => addSubsection(deptIndex, secIndex)}>Add</Button>
                          </div>

                          {section.subsections?.map((sub, subIdx) => {
                            if (sub.name === 'Frontend UI') {
                              console.log('✅ RENDERING FRONTEND UI SUBSECTION:', sub);
                            }
                            return (
                              <div key={subIdx} style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
                                <InputField
                                  value={sub.name}
                                  onChange={({ value }) => updateSubsectionName(deptIndex, secIndex, subIdx, value)}
                                  style={{ flex: 1 }}
                                  required
                                  placeholder="Subsection name"
                                />
                                <Button small destructive onClick={() => removeSubsection(deptIndex, secIndex, subIdx)} style={{ marginLeft: 8 }}>
                                  Remove
                                </Button>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: 25, marginBottom: 25, padding: 20, border: '1px solid #e0e0e0', borderRadius: 8 }}>
        <OrganisationAssignDrop
          programs={programs}
          orgUnits={allOrgUnits}
          programAssignments={programAssignments}
          onAssignmentChange={setProgramAssignments}
          onSaveAssignments={saveAllProgramAssignments}
          loading={programAssignmentLoading}
          currentUserOrgUnitId={orgUnitId}
          showSaveButton={false}
          saveManagedByParent={true}
        />
      </div>

      <div style={{
        padding: '12px 16px',
        backgroundColor: '#e8f5e9',
        border: '1px solid #c8e6c9',
        borderRadius: 8,
        color: '#2e7d32',
        marginBottom: 20
      }}>
        Performance assignment changes will be saved automatically when you click <strong>Update Company</strong>.
      </div>

      <Button
        primary
        type="submit"
        disabled={updating || isSubmitting}
        style={{ marginTop: 20, minWidth: 220 }}
      >
        {updating || isSubmitting ? (
          <>
            <CircularLoader small /> Please wait saving...
          </>
        ) : (
          'Update Company'
        )}
      </Button>

      <div style={{
        marginTop: 40,
        padding: 20,
        borderTop: '1px solid #e0e0e0',
        textAlign: 'center'
      }}>
        <h3>Company Goals Management</h3>
        <p>As an administrator, you can set up and manage company-wide goals</p>
        <Button
          primary
          onClick={() => navigate('/goals-management')}
          style={{ marginTop: 10 }}
        >
          Manage Company Goals
        </Button>
        {!allOrgUnitsAssigned && (
          <p style={{
            color: '#f44336',
            fontSize: '14px',
            marginTop: '10px',
            fontStyle: 'italic'
          }}>
            ⚠️ Please assign all departments and sections to the performance assessment above before managing goals.
          </p>
        )}
      </div>
    </form>
  );
};

export default OrganisationUpdate;
